<template>
    <Row
        class="CustomPropertyCollateralRow"
        :class="{
            'CustomPropertyCollateralRow--focus': isAnyFieldFocused,
            'CustomPropertyCollateralRow--readOnly': !editingEnabled,
            'CustomPropertyCollateralRow--disabled': rbacDisabled
        }"
    >
        <Cell
            v-for="({ name }) in columns"
            :key="name"
            :class="`CustomPropertyCollateralRow__${name}Cell`"
            :data-test-id="`CustomPropertyCollateralRow__${name}Cell`"
        >
            <template v-if="name==='action'">
                <div v-if="editingEnabled" class="flex gap-1">
                    <LockInputIcon v-if="rbacDisabled" class="CustomPropertyCollateralRow__actionButton" />
                    <NewButton
                        v-else
                        variant="flat"
                        size="icon-sm"
                        color="neutral"
                        :disabled="updatingInsured"
                        data-test-id="CustomPropertyCollateralRow__deleteButton"
                        @click="onClickDelete"
                    >
                        <EidIcon :icon-src="faXmark" alt="delete" />
                    </NewButton>
                </div>
            </template>
            <span
                v-else-if="!editingEnabled"
                class="CustomPropertyCollateralRow__value"
                :data-test-id="`CustomPropertyCollateralRow__${name}Value`"
            >{{ readOnlyValue(name) }}</span>
            <JsonSchemaFormElement
                v-else
                v-model="localCollateral[name]"
                :form="fields[name].form"
                :touched="touched"
                :disabled="rbacDisabled"
                :required="form.schema.required.includes(name)"
                :custom-component-input="getCustomComponent"
                @focus="onFocusField(true)"
                @blur="onFocusField(false)"
            />
        </Cell>
    </Row>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import isEqual from 'lodash/isEqual';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { JsonSchemaFormElement } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { Row, Cell } from '@evidentid/dashboard-commons/components/Table';
    import createForm from '@evidentid/json-schema/createForm';
    import { JsonFormProperty } from '@evidentid/json-schema/interfaces/JsonForm';
    import {
        JsonSchemaAbstractObject,
        JsonSchemaArray,
    } from '@evidentid/json-schema/interfaces/JsonSchema';
    import {
        TprmSchemaDisplayFormat,
    } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CustomPropertyCollateral } from '@evidentid/tprm-portal-lib/models/dashboard/CustomProperty.model';

    import NewButton from '@/components/new-button/NewButton.vue';
    import CriterionCurrencyInput
        from '@/modules/decisioning-criteria/components/CriterionInputComponents/CriterionCurrencyInput.vue';
    import { TableColumn } from '@/modules/entity-details/models/TableColumn.model';
    import { currencyFormatter } from '@/modules/entity-details/utils/currencyFormatter';
    import { getJsonSchemaCompFunction } from '@/utils/getJsonSchemaCustomCompFuntion';

    const getCustomComponent = getJsonSchemaCompFunction({
        [TprmSchemaDisplayFormat.currency]: CriterionCurrencyInput,
    });
    export default defineComponent({
        name: 'CustomPropertyCollateralRow',
        components: {
            JsonSchemaFormElement,
            Cell,
            EidIcon,
            Row,
            LockInputIcon,
            NewButton,
        },
        props: {
            customPropertyCollaterals: {
                type: Object as PropType<CustomProperty>,
                required: true,
            },
            collateral: {
                type: Object as PropType<CustomPropertyCollateral>,
                default: (): CustomPropertyCollateral => ({}) as CustomPropertyCollateral,
            },
            updatingInsured: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            columns: {
                type: Array as PropType<TableColumn[]>,
                required: true,
            },
            rbacDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            editingEnabled: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
            touched: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'delete', 'input' ],
        data() {
            const schema = (this.customPropertyCollaterals.schema as JsonSchemaArray<JsonSchemaAbstractObject>).items;
            const form = createForm(schema);
            const newCollateralData = form.getValue(undefined, true);
            return {
                faXmark,
                form,
                getCustomComponent,
                isAnyFieldFocused: false,
                localCollateral: {} as CustomPropertyCollateral,
            };
        },
        computed: {
            fields(): Record<string, JsonFormProperty> {
                const properties = this.form.getProperties();
                return properties.reduce((acc, property) => {
                    acc[property.name] = property;
                    return acc;
                }, {} as Record<string, JsonFormProperty>);
            },
        },
        watch: {
            collateral: {
                immediate: true,
                handler(newValue?: boolean) {
                    if (newValue) {
                        this.localCollateral = { ...this.collateral };
                    }
                },
            },
            localCollateral: {
                deep: true,
                handler() {
                    // Emit on every field change when in global edit mode.
                    // isEqual guard prevents looping when the parent syncs the prop back.
                    if (this.editingEnabled && !isEqual(this.localCollateral, this.collateral)) {
                        this.$emit('input', { ...this.localCollateral });
                    }
                },
            },
        },
        methods: {
            /**
             * Formats a field for the read-only row.
             *
             * The row renders text rather than inputs outside edit mode because `value-only` is
             * honoured by only one form element — the currency controls ignore it and stay
             * editable, so relying on it would leave the limit and deductible cells writable when
             * the entity is not being edited.
             */
            readOnlyValue(name: string): string {
                const value = (this.collateral as Record<string, unknown>)[name];
                if (value == null || value === '') {
                    return '';
                }
                if (name === 'limitRequired' || name === 'maximumDeductible') {
                    return currencyFormatter().format(Number(value));
                }
                return String(value);
            },
            onFocusField(focused: boolean): void {
                this.isAnyFieldFocused = focused;
            },
            onClickDelete(): void {
                this.$emit('delete');
            },
        },
    });
</script>
